import React, { useState } from 'react';
import { ChevronRightIcon, CloudArrowUpIcon, CpuChipIcon, ServerIcon, ShieldCheckIcon, PhotoIcon, TagIcon, ClockIcon, MapPinIcon } from '@heroicons/react/24/outline';

const App = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const scrollToWaitlist = () => {
    document.getElementById('waitlist').scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        console.error('Failed to join waitlist');
      }
    } catch (error) {
      console.error('Error joining waitlist:', error);
    }
  };

  return (
    <div className="min-h-screen bg-cream text-olive">
      {/* Hero Section */}
      <section className="relative px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-8">
            <img
              src="/logo.jpeg"
              alt="moosh-il logo"
              className="mx-auto mb-6 h-48 w-48 rounded-full object-cover"
            />
            <h1 className="text-5xl font-light tracking-tight text-olive sm:text-7xl lg:text-8xl">
              moosh-il
            </h1>
            <div className="mt-4 h-px w-24 bg-cream-darker mx-auto"></div>
          </div>

          <h2 className="text-2xl font-light leading-relaxed text-olive-light sm:text-3xl lg:text-4xl mb-8">
            The Intelligent Second-Hand Fashion Platform for Israel
            <br />
            <span className="text-lg text-cream0 mt-4 block">
              AI-Powered Boutique Marketplace
            </span>
          </h2>

          <p className="mx-auto max-w-2xl text-lg leading-relaxed text-olive-light mb-12">
            Transform your closet into profit. Just snap a photo—our AI handles categorization,
            sizing, and descriptions instantly. Built for Israeli sellers who demand premium quality.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={scrollToWaitlist}
              className="bg-olive text-cream px-8 py-4 text-lg font-medium hover:bg-olive-dark transition-colors"
            >
              Join Waitlist
            </button>
            <button className="border border-cream-darker text-olive-light px-8 py-4 text-lg font-medium hover:bg-cream-dark transition-colors">
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* AI Innovation Section */}
      <section className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24 bg-cream">
        <div className="mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-light text-olive mb-6">
              AI That Actually Works
            </h3>
            <p className="text-xl text-olive-light max-w-3xl mx-auto">
              Upload once. Sell everywhere. Our computer vision instantly analyzes your items
              and generates perfect listings.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-cream-dark rounded-full flex items-center justify-center mx-auto mb-6">
                <PhotoIcon className="w-10 h-10 text-olive-light" />
              </div>
              <h4 className="text-2xl font-medium text-olive mb-4">
                1. Snap Photo
              </h4>
              <p className="text-olive-light leading-relaxed">
                Take a quick photo with your phone. Our AI works with any lighting,
                any angle, any background.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-cream-dark rounded-full flex items-center justify-center mx-auto mb-6">
                <CpuChipIcon className="w-10 h-10 text-olive-light" />
              </div>
              <h4 className="text-2xl font-medium text-olive mb-4">
                2. AI Analyzes
              </h4>
              <p className="text-olive-light leading-relaxed">
                Amazon Bedrock identifies brand, size, condition, style, and generates
                compelling product descriptions.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-20 h-20 bg-cream-dark rounded-full flex items-center justify-center mx-auto mb-6">
                <TagIcon className="w-10 h-10 text-olive-light" />
              </div>
              <h4 className="text-2xl font-medium text-olive mb-4">
                3. Instant Listing
              </h4>
              <p className="text-olive-light leading-relaxed">
                Perfect listing created in seconds. Edit if needed, or publish
                immediately to reach Israeli buyers.
              </p>
            </div>
          </div>

          {/* Demo Visual */}
          <div className="mt-16 bg-cream-dark rounded-lg p-8 lg:p-12">
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
              <div className="flex-1">
                <div className="bg-white rounded-lg shadow-sm border border-cream-darker p-6">
                  <div className="w-full h-48 bg-cream-dark rounded mb-4 flex items-center justify-center">
                    <PhotoIcon className="w-16 h-16 text-cream-dark" />
                  </div>
                  <p className="text-sm text-cream0 text-center">Upload your photo</p>
                </div>
              </div>

              <div className="flex-shrink-0">
                <ChevronRightIcon className="w-8 h-8 text-cream-dark transform rotate-90 lg:rotate-0" />
              </div>

              <div className="flex-1">
                <div className="bg-white rounded-lg shadow-sm border border-cream-darker p-6">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-cream0">Brand:</span>
                      <span className="text-sm font-medium">Zara</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-cream0">Size:</span>
                      <span className="text-sm font-medium">M</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-cream0">Condition:</span>
                      <span className="text-sm font-medium">Like New</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-cream0">Category:</span>
                      <span className="text-sm font-medium">Dress</span>
                    </div>
                    <div className="pt-3 border-t border-cream-darker">
                      <p className="text-sm text-olive-light">
                        Elegant Zara dress in excellent condition. Perfect for special occasions...
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Locker Delivery Section */}
      <section className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24 bg-white">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="flex-1">
              <MapPinIcon className="w-16 h-16 text-olive mb-6" />
              <h3 className="text-4xl font-light text-olive mb-6">
                Easy Transfer with Smart Lockers
              </h3>
              <p className="text-xl text-olive-light mb-6 leading-relaxed">
                No more awkward meetups or shipping hassles. Drop off and pick up items at
                secure locker locations across Israel.
              </p>
              <ul className="space-y-4 text-lg text-olive-light">
                <li className="flex items-start">
                  <span className="text-olive font-medium mr-2">✓</span>
                  <span>24/7 access at hundreds of locations nationwide</span>
                </li>
                <li className="flex items-start">
                  <span className="text-olive font-medium mr-2">✓</span>
                  <span>Secure, climate-controlled storage</span>
                </li>
                <li className="flex items-start">
                  <span className="text-olive font-medium mr-2">✓</span>
                  <span>No face-to-face contact required</span>
                </li>
                <li className="flex items-start">
                  <span className="text-olive font-medium mr-2">✓</span>
                  <span>Automatic notifications when ready for pickup</span>
                </li>
              </ul>
            </div>
            <div className="flex-1">
              <div className="bg-cream-dark rounded-lg p-8 lg:p-12">
                <div className="text-center">
                  <div className="inline-block bg-olive rounded-full p-6 mb-6">
                    <MapPinIcon className="w-12 h-12 text-cream" />
                  </div>
                  <h4 className="text-2xl font-medium text-olive mb-4">
                    Smart Locker Network
                  </h4>
                  <p className="text-olive-light mb-6">
                    Partnered with Israel's leading locker providers for seamless transfers.
                  </p>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-3xl font-light text-olive mb-2">300+</div>
                      <div className="text-sm text-olive-light">Locations</div>
                    </div>
                    <div>
                      <div className="text-3xl font-light text-olive mb-2">24/7</div>
                      <div className="text-sm text-olive-light">Access</div>
                    </div>
                    <div>
                      <div className="text-3xl font-light text-olive mb-2">100%</div>
                      <div className="text-sm text-olive-light">Secure</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AWS Architecture Section */}
      <section className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24 bg-olive text-white">
        <div className="mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-light mb-6">
              Built on AWS
            </h3>
            <p className="text-xl text-cream max-w-3xl mx-auto">
              Enterprise-grade infrastructure that scales with Israeli fashion.
              Secure, fast, and ready for millions of listings.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Bedrock */}
            <div className="bg-olive-dark rounded-lg p-6">
              <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center mb-4">
                <CpuChipIcon className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-xl font-medium mb-3">Amazon Bedrock</h4>
              <p className="text-cream leading-relaxed">
                Advanced computer vision and natural language processing for instant
                item analysis and professional description generation.
              </p>
            </div>

            {/* S3 */}
            <div className="bg-olive-dark rounded-lg p-6">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4">
                <CloudArrowUpIcon className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-xl font-medium mb-3">Amazon S3</h4>
              <p className="text-cream leading-relaxed">
                Secure, high-speed image storage with CDN delivery.
                Lightning-fast photo uploads and retrieval across Israel.
              </p>
            </div>

            {/* ECS Fargate */}
            <div className="bg-olive-dark rounded-lg p-6">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                <ServerIcon className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-xl font-medium mb-3">Amazon ECS Fargate</h4>
              <p className="text-cream leading-relaxed">
                Serverless containerized microservices. Auto-scaling architecture
                handles traffic spikes during peak selling seasons.
              </p>
            </div>

            {/* RDS */}
            <div className="bg-olive-dark rounded-lg p-6">
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-4">
                <ServerIcon className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-xl font-medium mb-3">Amazon RDS</h4>
              <p className="text-cream leading-relaxed">
                Managed PostgreSQL for robust listing data, user profiles,
                and transaction history with automated backups.
              </p>
            </div>

            {/* CloudFormation */}
            <div className="bg-olive-dark rounded-lg p-6">
              <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center mb-4">
                <ClockIcon className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-xl font-medium mb-3">AWS CloudFormation</h4>
              <p className="text-cream leading-relaxed">
                Infrastructure-as-Code for consistent deployments.
                Reproducible environments from development to production.
              </p>
            </div>

            {/* Secrets Manager */}
            <div className="bg-olive-dark rounded-lg p-6">
              <div className="w-12 h-12 bg-yellow-600 rounded-lg flex items-center justify-center mb-4">
                <ShieldCheckIcon className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-xl font-medium mb-3">AWS Secrets Manager</h4>
              <p className="text-cream leading-relaxed">
                Enterprise-grade security for API keys, database credentials,
                and sensitive configuration. SOC 2 compliant.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-lg text-cream">
              Ready to scale from Tel Aviv to Eilat. Built for the Israeli startup ecosystem.
            </p>
          </div>
        </div>
      </section>

      {/* Contact/Waitlist Section */}
      <section id="waitlist" className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24 bg-cream">
        <div className="mx-auto max-w-2xl text-center">
          <h3 className="text-4xl font-light text-olive mb-6">
            Join the Revolution
          </h3>
          <p className="text-xl text-olive-light mb-12">
            Be among the first Israeli sellers to experience AI-powered fashion commerce.
            Early access includes premium features and zero commission for 6 months.
          </p>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@gmail.com"
                  required
                  className="w-full px-6 py-4 text-lg border border-cream-darker focus:border-olive focus:outline-none bg-white"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-olive text-white px-8 py-4 text-lg font-medium hover:bg-olive-dark transition-colors"
              >
                Join Waitlist
              </button>
            </form>
          ) : (
            <div className="bg-cream-dark rounded-lg p-8">
              <h4 className="text-2xl font-medium text-olive mb-4">
                Thank You! Welcome to moosh-il
              </h4>
              <p className="text-olive-light">
                You're on the waitlist. We'll notify you when early access opens.
              </p>
            </div>
          )}

          <div className="mt-12 flex flex-col sm:flex-row gap-6 justify-center text-sm text-cream0">
            <span>🇮🇱 Made in Israel</span>
            <span>☁️ Powered by AWS</span>
            <span>🤖 Enhanced by AI</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-olive text-cream-dark px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <img
              src="/logo.jpeg"
              alt="moosh-il logo"
              className="mx-auto mb-4 h-24 w-24 rounded-full object-cover"
            />
            <h4 className="text-2xl font-light text-white mb-4">moosh-il</h4>
            <p className="mb-8">
              Boutique second-hand fashion marketplace for Israel
            </p>
            <div className="flex justify-center space-x-6">
              <a href="#" className="hover:text-cream transition-colors">Privacy</a>
              <a href="#" className="hover:text-cream transition-colors">Terms</a>
              <a href="#" className="hover:text-cream transition-colors">Contact</a>
            </div>
            <div className="mt-8 pt-8 border-t border-olive-light">
              <p className="text-sm">
                © 2024 moosh-il. Built for AWS Startup Credits Program.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;